import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { Input, Button, Card, message } from "antd";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Lottie from "lottie-react";
import { EsterService } from '../../services/ester-service';
import {
  NAV_STYLE_MINI_SIDEBAR,
  NAV_STYLE_NO_HEADER_MINI_SIDEBAR,
  NAV_STYLE_DRAWER,
  TAB_SIZE
} from "../../constants/ThemeSetting";
import "./style.css";

const { TextArea } = Input;

const EsterChatModal = ({ 
  isOpen, 
  onClose, 
  position = { bottom: "-10px", right: "10px" } 
}) => {
  const { navStyle, width, navCollapsed } = useSelector((state) => state.settings);
  
  // Calculate sidebar width based on state
  const getSidebarWidth = () => {
    // Drawer mode: sidebar is hidden when drawer is closed, visible when open
    if (width < TAB_SIZE || navStyle === NAV_STYLE_DRAWER) {
      return navCollapsed ? 280 : 0; // Drawer width when open, 0 when closed
    }
    
    // Fixed sidebar mode: check if collapsed
    const isCollapsed = navStyle === NAV_STYLE_MINI_SIDEBAR || 
                        navStyle === NAV_STYLE_NO_HEADER_MINI_SIDEBAR;
    return isCollapsed ? 80 : 280; // @sidebar-mini-drawer-width : @sidebar-width
  };

  const sidebarWidth = getSidebarWidth();
  const modalLeftPosition = sidebarWidth + 10; // 10px gap from sidebar

  const [messages, setMessages] = useState([
    {
      id: 1,
      role: "assistant",
      content: "Hi! I'm Ester, your ClarityAI assistant. How can I help you today?",
      timestamp: new Date().toISOString()
    }
  ]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [chatSession, setChatSession] = useState(null);
  const [streamingMessageId, setStreamingMessageId] = useState(null);
  const [streamingContent, setStreamingContent] = useState('');
  
  const messagesEndRef = useRef(null);
  const streamingRef = useRef(null);
  const debouncedScrollTimeout = useRef(null);

  useEffect(() => {
    if (isOpen) {
      const validation = EsterService.validateConfig();
      if (!validation.valid) {
        message.error(validation.message, 10);
      }
    }
  }, [isOpen]);

  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  useEffect(() => {
    if (debouncedScrollTimeout.current) {
      clearTimeout(debouncedScrollTimeout.current);
    }
    debouncedScrollTimeout.current = setTimeout(scrollToBottom, 100);
    
    return () => {
      if (debouncedScrollTimeout.current) {
        clearTimeout(debouncedScrollTimeout.current);
      }
    };
  }, [messages, streamingContent]);

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === "Escape" && isOpen) {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscKey);
    return () => document.removeEventListener("keydown", handleEscKey);
  }, [isOpen, onClose]);

  const MessageContent = ({ message }) => {
    const baseMessageStyle = {
      maxWidth: '80%',
      padding: '10px 14px',
      borderRadius: '18px',
      fontSize: '13px',
      lineHeight: '1.5',
      wordBreak: 'break-word'
    };

    const messageStyle = {
      ...baseMessageStyle,
      backgroundColor: message.isError 
        ? '#ffebee' 
        : message.role === 'user' ? '#000000' : '#f0f0f0',
      color: message.isError 
        ? '#c62828' 
        : message.role === 'user' ? 'white' : '#000000',
      border: message.isError ? '1px solid #ffcdd2' : 'none'
    };

    const markdownComponents = {
      h1: ({node, ...props}) => <h1 style={{ fontSize: '15px', fontWeight: 'bold', margin: '10px 0 5px 0', color: '#1a1a1a' }} {...props} />,
      h2: ({node, ...props}) => <h2 style={{ fontSize: '14px', fontWeight: 'bold', margin: '8px 0 4px 0', color: '#1a1a1a' }} {...props} />,
      h3: ({node, ...props}) => <h3 style={{ fontSize: '13px', fontWeight: 'bold', margin: '6px 0 3px 0', color: '#1a1a1a' }} {...props} />,
      p: ({node, ...props}) => <p style={{ margin: '3px 0', lineHeight: '1.5', fontSize: '13px' }} {...props} />,
      ul: ({node, ...props}) => <ul style={{ margin: '5px 0', paddingLeft: '16px', fontSize: '13px' }} {...props} />,
      ol: ({node, ...props}) => <ol style={{ margin: '5px 0', paddingLeft: '16px', fontSize: '13px' }} {...props} />,
      li: ({node, ...props}) => <li style={{ margin: '1px 0', lineHeight: '1.4', fontSize: '13px' }} {...props} />,
      strong: ({node, ...props}) => <strong style={{ fontWeight: 'bold', color: '#1a1a1a' }} {...props} />,
      code: ({node, inline, ...props}) => {
        const codeStyle = {
          backgroundColor: 'rgba(0,0,0,0.1)',
          padding: inline ? '1px 3px' : '6px 10px',
          borderRadius: '3px',
          fontSize: '11px',
          fontFamily: 'monospace',
          display: inline ? 'inline' : 'block',
          overflowX: 'auto'
        };
        return <code style={codeStyle} {...props} />;
      },
      pre: ({node, ...props}) => <pre style={{ margin: '6px 0', overflowX: 'auto' }} {...props} />
    };

    if (message.role === 'assistant' && !message.isError) {
      const contentToRender = message.isStreaming && message.id === streamingMessageId 
        ? streamingContent 
        : message.content;
      
      return (
        <div style={messageStyle}>
          <div style={{ position: 'relative', display: 'inline-block', width: '100%' }}>
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={markdownComponents}
            >
              {contentToRender}
            </ReactMarkdown>
            {message.isStreaming && (
              <span className="streaming-cursor">|</span>
            )}
          </div>
        </div>
      );
    }

    return (
      <div style={messageStyle}>
        {message.content}
      </div>
    );
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;
    
    const userMessage = inputValue.trim();
    setInputValue('');
    setIsLoading(true);
    
    const newUserMessage = { 
      id: Date.now(), 
      role: 'user', 
      content: userMessage, 
      timestamp: new Date().toISOString() 
    };
    setMessages(prev => [...prev, newUserMessage]);
    
    try {
      let currentSession = chatSession;
      
      if (!currentSession || !currentSession.threadId) {
        currentSession = await EsterService.initializeEsterChat();
        
        if (!currentSession || !currentSession.threadId) {
          throw new Error('Failed to create chat session - no thread ID returned');
        }
        
        setChatSession(currentSession);
      }
      
      await EsterService.addUserMessage(currentSession.threadId, userMessage);
      
      const aiMessageId = Date.now() + 1;
      const aiMessage = { 
        id: aiMessageId, 
        role: 'assistant', 
        content: '', 
        timestamp: new Date().toISOString(),
        isStreaming: true
      };
      setMessages(prev => [...prev, aiMessage]);
      setStreamingMessageId(aiMessageId);
      setIsLoading(false);
      
      let accumulatedContent = '';
      
      await EsterService.runAssistantStream(
        currentSession.threadId,
        (chunk) => {
          accumulatedContent += chunk;
          setStreamingContent(accumulatedContent);
        },
        () => {
          setMessages(prev => prev.map(msg => 
            msg.id === aiMessageId 
              ? { ...msg, content: accumulatedContent, isStreaming: false }
              : msg
          ));
          
          setStreamingMessageId(null);
          setStreamingContent('');
          setIsLoading(false);
        },
        (error) => {
          setMessages(prev => prev.map(msg => 
            msg.id === aiMessageId 
              ? {
                  ...msg,
                  content: `I apologize, but I'm having trouble connecting right now. ${error.message.includes('API key') ? 'Please check the API configuration.' : error.message.includes('Assistant ID') ? 'Please check the Assistant ID configuration.' : 'Please try again in a moment.'}`,
                  isError: true,
                  isStreaming: false
                }
              : msg
          ));
          
          setStreamingMessageId(null);
          setStreamingContent('');
          setIsLoading(false);
          message.error('Unable to get response from Ester. Please try again.');
        }
      );
      
    } catch (error) {
      
      const errorMessage = { 
        id: Date.now() + 1, 
        role: 'assistant', 
        content: `I apologize, but I'm having trouble connecting right now. ${error.message.includes('API key') ? 'Please check the API configuration.' : error.message.includes('Assistant ID') ? 'Please check the Assistant ID configuration.' : error.message}`, 
        timestamp: new Date().toISOString(),
        isError: true
      };
      
      setMessages(prev => [...prev, errorMessage]);
      setIsLoading(false);
      message.error('Unable to get response from Ester. Please try again.');
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleClearChat = () => {
    setMessages([
      {
        id: Date.now(),
        role: "assistant",
        content: "Hi! I'm Ester, your ClarityAI assistant. How can I help you today?",
        timestamp: new Date().toISOString()
      }
    ]);
    setChatSession(null);
    message.success('Chat cleared');
  };

  const isInputDisabled = isLoading || streamingMessageId;

  if (!isOpen) return null;

  return (
    <Card
      className="ester-chat-modal"
      style={{
        position: "fixed",
        bottom: position.bottom || "60px",
        left: position.left !== undefined ? position.left : `${modalLeftPosition}px`,
        right: position.right,
        top: position.top,
        width: "390px",
        maxWidth: "90vw",
        height: "530px",
        maxHeight: "80vh",
        zIndex: 1001,
        display: "flex",
        flexDirection: "column",
        padding: 0,
        boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
        borderRadius: "16px",
        border: "1px solid #e8e8e8",
        overflow: "hidden",
        backgroundColor: "#ffffff",
        transition: "left 0.3s ease"
      }}
      bodyStyle={{
        padding: 0,
        height: "100%",
        display: "flex",
        flexDirection: "column"
      }}
    >
      <div className="ester-chat-header">
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <div className="ester-avatar-container">
            <Lottie
              animationData={require("../../assets/animations/ester-lottie.json")}
              loop={true}
              autoplay={true}
              style={{ width: "100%", height: "100%" }}
            />
          </div>
          <div>
            <div style={{ fontWeight: "600", fontSize: "18px", color: "#000000", lineHeight: "1.2" }}>Ester</div>
            <div style={{ fontSize: "13px", color: "#8c8c8c", lineHeight: "1.2" }}>ClarityAI Assistant</div>
          </div>
        </div>
        <button
          onClick={onClose}
          className="ester-close-button"
          aria-label="Close chat"
        >
          ×
        </button>
      </div>

      <div className="ester-messages-container">
        {messages.map((message) => (
          <div
            key={message.id}
            style={{
              marginBottom: "16px",
              display: "flex",
              justifyContent: message.role === "user" ? "flex-end" : "flex-start"
            }}
          >
            <MessageContent message={message} />
          </div>
        ))}
        
        {isLoading && !streamingMessageId && (
          <div className="ester-loading-indicator">
            <div className="ester-loading-message">
              <div className="ester-typing-dots">
                <div className="dot"></div>
                <div className="dot"></div>
                <div className="dot"></div>
              </div>
              <span style={{ opacity: 0.7, marginLeft: "8px" }}>Ester is thinking...</span>
            </div>
          </div>
        )}
        
        <div ref={messagesEndRef} />
      </div>

      <div className="ester-input-container">
        <div className="ester-input-wrapper">
          <input
            type="text"
            placeholder={streamingMessageId ? "Ester is responding..." : isLoading ? "Ester is thinking..." : "Ask Ester"}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyPress}
            disabled={isInputDisabled}
            className="ester-input-field"
          />
          <button
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isInputDisabled}
            className="ester-send-button"
          >
            {isLoading ? (
              <div className="ester-loading-spinner" />
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
              </svg>
            )}
          </button>
        </div>
      </div>
    </Card>
  );
};

export default EsterChatModal;

